import { makeStyles } from 'tss-react/mui';
import { Timesheet, TimesheetDay } from 'api/report/types';
import { TimesheetSettingsType } from 'api/customer/types';
import { TimesheetReportTemplate } from 'utils/report';
import {
    TravelsheetKeyType,
    Summary,
} from 'report/logic/timesheet/calculations';
import TravelsheetComponent from './Travelsheet';
import TimesheetComponent from './Timesheet';
import NotFound from 'components/interface/NotFound/NotFound';
import { LoadingSpinner } from 'components/interface/LoadingSpinner';

const useStyles = makeStyles()(theme => ({
    base: {
        marginBottom: theme.spacing(4),
    },

    spinner: {
        marginTop: theme.spacing(2),
    },
}));

interface TimesheetProps {
    timesheet: Timesheet | null;
    totalHours: string;
    template: TimesheetReportTemplate | null;
    summary: Summary;
    timesheetSetting: TimesheetSettingsType;
    canSubmit: boolean;
    disabled: boolean;
    allowTravelsheet: boolean;
    loading: boolean;
    error: boolean;
    enableSickHours: boolean;
    onEnableSickHours: (value: boolean) => void;
    onUpdateTimesheetDay: (
        updateFunc: (day: TimesheetDay) => TimesheetDay,
        index: number
    ) => void;
    onTravelsheetFieldChange: (
        key: TravelsheetKeyType,
        index: number
    ) => (value: string) => void;
    onNotesFieldChange: (value: string) => void;
    useSplitTextfields: boolean;

    commentContainsSickHints: boolean;
    sickHoursDuplicateDays: TimesheetDay[];
}

const View = ({
    timesheet,
    totalHours,
    template,
    summary,
    timesheetSetting,
    canSubmit,
    disabled,
    allowTravelsheet,
    loading,
    error,
    enableSickHours,
    onEnableSickHours,
    onUpdateTimesheetDay,
    onTravelsheetFieldChange,
    onNotesFieldChange,
    useSplitTextfields,

    commentContainsSickHints,
    sickHoursDuplicateDays,
}: TimesheetProps): JSX.Element => {
    const { classes } = useStyles();

    const renderPage = () =>
        !timesheet || !template ? (
            <></>
        ) : (
            <div className={classes.base}>
                <TimesheetComponent
                    timesheetSetting={timesheetSetting}
                    timesheet={timesheet}
                    totalHours={totalHours}
                    template={template}
                    summary={summary}
                    canSubmit={canSubmit}
                    disabled={disabled}
                    loading={loading}
                    enableSickHours={enableSickHours}
                    onEnableSickHours={onEnableSickHours}
                    onUpdateTimesheetDay={onUpdateTimesheetDay}
                    onNotesFieldChange={onNotesFieldChange}
                    useSplitTextfields={useSplitTextfields}
                    commentContainsSickHints={commentContainsSickHints}
                    sickHoursDuplicateDays={sickHoursDuplicateDays}
                />
                {allowTravelsheet && timesheet?.travelsheet ? (
                    <TravelsheetComponent
                        travelsheet={timesheet?.travelsheet}
                        canSubmit={canSubmit}
                        disabled={disabled}
                        onTravelsheetFieldChange={onTravelsheetFieldChange}
                    />
                ) : null}
            </div>
        );

    return error ? (
        <NotFound variant='error' isMobile={false} />
    ) : (
        <LoadingSpinner
            loading={loading || !timesheet}
            className={classes.spinner}
            size={75}>
            {renderPage()}
        </LoadingSpinner>
    );
};

export default View;
